import { api } from '../../lib/api-client';

export type StockCountStatus = 'COUNTING' | 'SUBMITTED' | 'APPROVED' | 'CANCELLED';

export interface StockCountLineView {
  uid: string;
  skuUid: string;
  skuCode: string;
  barcode: string | null;
  name: string;
  countedQty: string | null;
  countedBy: string | null;
  countedAt: string | null;
  addedOnShelf: boolean;
  /** The keys below are absent for a counter without parts.stock_adjust (the count is blind). */
  systemQty?: string | null;
  difference?: string | null;
  differenceValue?: string | null;
  reserved?: string;
  belowReserved?: boolean;
  autocountQty?: string | null;
  autocountReadAt?: string | null;
}

export interface StockCountTotals {
  up: string;
  down: string;
  valueUp: string;
  valueDown: string;
}

export interface StockCountView {
  uid: string;
  countNo: string;
  status: StockCountStatus;
  locationUid: string;
  locationName: string;
  codePrefix: string | null;
  note: string | null;
  createdAt: string;
  createdBy: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  cancelReason: string | null;
  progress: { counted: number; total: number };
  lines: StockCountLineView[];
  /** Managers only. */
  totals?: StockCountTotals;
  canCount: boolean;
  canSubmit: boolean;
  canApprove: boolean;
  canSendBack: boolean;
  canCancel: boolean;
  blind: boolean;
}

export interface StockCountListItem {
  uid: string;
  countNo: string;
  status: StockCountStatus;
  locationName: string;
  codePrefix: string | null;
  note: string | null;
  createdAt: string;
  createdBy: string | null;
  progress: { counted: number; total: number };
}

export interface StockCountPage {
  items: StockCountListItem[];
  nextCursor: string | null;
}

export interface AutocountDifference {
  skuCode: string;
  name: string;
  autocountLocationCode: string | null;
  countedQty: string;
  autocountQty: string;
  autocountReadAt: string | null;
}

export type { StockLocation } from '../../components/stock-locations';

export function listStockCounts(params: { status?: StockCountStatus; cursor?: string }): Promise<StockCountPage> {
  const search = new URLSearchParams();
  if (params.status !== undefined) search.set('status', params.status);
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<StockCountPage>(`/parts/counts${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getStockCount(uid: string): Promise<StockCountView> {
  return api<StockCountView>(`/parts/counts/${uid}`);
}

export function startStockCount(input: {
  locationUid: string;
  codePrefix?: string;
  note?: string;
}): Promise<StockCountView> {
  return api<StockCountView>('/parts/counts', { method: 'POST', body: input });
}

export function putStockCountLine(uid: string, input: { code: string; countedQty: string }): Promise<StockCountView> {
  return api<StockCountView>(`/parts/counts/${uid}/lines`, { method: 'PUT', body: input });
}

export function removeStockCountLine(uid: string, lineUid: string): Promise<StockCountView> {
  return api<StockCountView>(`/parts/counts/${uid}/lines/${lineUid}`, { method: 'DELETE' });
}

export function submitStockCount(uid: string, input: { note?: string }): Promise<StockCountView> {
  return api<StockCountView>(`/parts/counts/${uid}/transitions/submit`, { method: 'POST', body: input });
}

export function sendBackStockCount(uid: string, input: { reason: string }): Promise<StockCountView> {
  return api<StockCountView>(`/parts/counts/${uid}/transitions/send-back`, { method: 'POST', body: input });
}

export function cancelStockCount(uid: string, input: { reason: string }): Promise<StockCountView> {
  return api<StockCountView>(`/parts/counts/${uid}/transitions/cancel`, { method: 'POST', body: input });
}

/** The key is required: a double tap must not adjust stock twice. */
export function approveStockCount(
  uid: string,
  input: { reason?: string },
  idempotencyKey: string,
): Promise<StockCountView> {
  return api<StockCountView>(`/parts/counts/${uid}/transitions/approve`, {
    method: 'POST',
    body: input,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export function getAutocountDifferences(uid: string): Promise<AutocountDifference[]> {
  return api<AutocountDifference[]>(`/parts/counts/${uid}/autocount-differences`);
}

export { listStockLocations } from '../../components/stock-locations';

export function exportAutocountDifferencesCsv(uid: string): Promise<string> {
  return api<string>(`/parts/counts/${uid}/autocount-differences?format=csv`);
}
